import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Btn, Field, Input, Select, Empty } from "@/components/UI";
import { toast } from "sonner";

const TYPES = ["yarn_supplier", "knitter", "dyer", "compactor", "printer", "stitcher", "customer"];
const empty = { name: "", type: "knitter", phone: "", address: "", gst_no: "", opening_balance: 0 };

export default function PartiesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [f, setF] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("");

  const load = () => api.get("/parties").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const startEdit = (p) => {
    setEditing(p.id);
    setF({ name: p.name, type: p.type, phone: p.phone || "", address: p.address || "", gst_no: p.gst_no || "", opening_balance: p.opening_balance || 0 });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditing(null); setF(empty); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...f, opening_balance: Number(f.opening_balance) };
      if (editing) {
        await api.put(`/parties/${editing}`, payload);
        toast.success("Party updated");
      } else {
        await api.post("/parties", payload);
        toast.success("Party added");
      }
      cancelEdit();
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this party?")) return;
    await api.delete(`/parties/${id}`);
    load();
  };

  const shown = filter ? list.filter((p) => p.type === filter) : list;

  return (
    <div>
      <PageHeader label="Master" title="Parties & Vendors" subtitle="Yarn suppliers, knitters, dyers, compactors, stitchers, customers" />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title={editing ? "Edit Party" : "Add Party"} right={editing && (
          <button type="button" onClick={cancelEdit} className="label-caps hover:text-destructive">Cancel</button>
        )}>
          <form onSubmit={submit} className="space-y-3" data-testid="party-form">
            <Field label="Name"><Input data-testid="party-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
            <Field label="Type">
              <Select data-testid="party-type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}>
                {TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
              </Select>
            </Field>
            <Field label="Phone"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
            <Field label="Address"><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></Field>
            <Field label="GSTIN"><Input placeholder="15-char GSTIN (optional)" value={f.gst_no} onChange={(e) => setF({ ...f, gst_no: e.target.value.toUpperCase() })} data-testid="party-gst" /></Field>
            <Field label="Opening Balance (₹)"><Input type="number" step="0.01" value={f.opening_balance} onChange={(e) => setF({ ...f, opening_balance: e.target.value })} /></Field>
            <Btn data-testid="party-submit">{editing ? "Save Changes" : "Add Party"}</Btn>
          </form>
        </Card>

        <Card title="Party List" className="lg:col-span-2" right={
          <Select value={filter} onChange={(e) => setFilter(e.target.value)} className="w-40">
            <option value="">All types</option>
            {TYPES.map((t) => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
          </Select>
        }>
          <div className="table-scroll">
            <table className="table-dense w-full">
              <thead><tr><th>Name</th><th>Type</th><th>Phone</th><th className="text-right">Opening</th><th></th></tr></thead>
              <tbody>
                {shown.map((p) => (
                  <tr key={p.id} className={editing === p.id ? "bg-secondary" : ""} data-testid={`party-row-${p.id}`}>
                    <td>{p.name}</td>
                    <td><span className="label-caps">{p.type.replace("_", " ")}</span></td>
                    <td className="font-mono">{p.phone || "—"}</td>
                    <td className="tabular text-right">₹{p.opening_balance || 0}</td>
                    <td className="text-right whitespace-nowrap">
                      {isAdmin && (
                        <>
                          <button onClick={() => startEdit(p)} className="label-caps hover:text-foreground mr-3" data-testid={`party-edit-${p.id}`}>Edit</button>
                          <button onClick={() => del(p.id)} className="label-caps hover:text-destructive" data-testid={`party-del-${p.id}`}>Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {shown.length === 0 && <tr><td colSpan={5}><Empty text="No parties yet" /></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
